'use client';

import { createContext, useContext } from 'react';

/**
 * The element every DS portal renders into.
 *
 * `null` (the default) means "fall back to `document.body`", which is exactly
 * what the DS does today — so a tree with no provider behaves identically.
 *
 * A consumer that mounts the DS inside its own scoped root (a class that owns
 * the design tokens, `isolation: isolate`, the icon font scope, …) can point
 * every portal back inside that root by providing this context once, instead
 * of threading a prop through every call site.
 */
export const PortalContainerContext = createContext<Element | null>(null);

/**
 * Resolve the portal container for a component.
 *
 * Precedence: an explicit `portalContainer` prop wins, then the nearest
 * `PortalContainerContext` provider, then `document.body` (today's behaviour).
 *
 * Returns `null` during a server render, where `document` does not exist —
 * the same condition every portal call site already guards on, so a caller
 * checks the resolved target instead of `typeof document`.
 */
export function usePortalContainer(explicit?: Element | null): Element | null {
  const fromContext = useContext(PortalContainerContext);
  if (explicit) return explicit;
  if (fromContext) return fromContext;
  return typeof document !== 'undefined' ? document.body : null;
}
